import { AlertCircle, Search } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

declare global {
  interface Window {
    google?: typeof google;
    mapApiLoaded?: boolean;
    mapApiError?: boolean;
  }
}

interface Coordinates {
  lat: number;
  lng: number;
}

interface MapLocationWidgetProps {
  apiKey?: string;
  initialCoordinates?: Coordinates;
  withSearch?: boolean;
  onLocationChange?: (coordinates: Coordinates, address?: string) => void;
  height?: string;
  className?: string;
  zoom?: number;
  fitBounds?: boolean;
  disableDragging?: boolean;
}

const MapLocationWidget: React.FC<MapLocationWidgetProps> = ({
  apiKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY,
  initialCoordinates = { lat: 31.9544, lng: 35.9106 },
  withSearch = false,
  onLocationChange,
  height = "h-64",
  className = "",
  zoom = 15,
  fitBounds = false,
  disableDragging = false,
}) => {
  const [coordinates, setCoordinates] =
    useState<Coordinates>(initialCoordinates);
  const [currentAddress, setCurrentAddress] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  const [mapError, setMapError] = useState<string | null>(null);

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const autoCompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const initialRenderRef = useRef<boolean>(true);
  const lastNotifiedCoordsRef = useRef<Coordinates | null>(null);

  // This function updates the address based on coordinates and notifies the parent
  const updateAddressFromCoordinates = useCallback(
    (coords: Coordinates) => {
      if (!geocoderRef.current || !window.google) return;

      setIsLoading(true);

      geocoderRef.current.geocode(
        { location: coords },
        (
          results: google.maps.GeocoderResult[] | null,
          status: google.maps.GeocoderStatus
        ) => {
          setIsLoading(false);

          const address =
            status === "OK" && results && results[0]?.formatted_address;

          const formattedAddress =
            address || `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`;
          setCurrentAddress(formattedAddress);

          // Store the last notified coordinates to prevent loops
          lastNotifiedCoordsRef.current = coords;

          // Notify parent with the updated address
          if (onLocationChange) {
            onLocationChange(coords, formattedAddress);
          }
        }
      );
    },
    [onLocationChange]
  );

  // Handle initialCoordinates prop changes
  useEffect(() => {
    // Skip initial render to avoid duplicate calls
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Skip if initialCoordinates match the last notified coordinates to prevent loops
    if (
      lastNotifiedCoordsRef.current &&
      lastNotifiedCoordsRef.current.lat === initialCoordinates.lat &&
      lastNotifiedCoordsRef.current.lng === initialCoordinates.lng
    ) {
      return;
    }

    // Update internal state
    setCoordinates(initialCoordinates);

    // Update map and marker if available
    if (mapInstanceRef.current && markerRef.current) {
      mapInstanceRef.current.setCenter(initialCoordinates);
      markerRef.current.setPosition(initialCoordinates);
    }

    // Get address for new coordinates if geocoder is available
    if (mapLoaded && geocoderRef.current && window.google) {
      updateAddressFromCoordinates(initialCoordinates);
    } else {
      // If map isn't loaded yet, store coords to prevent loops but don't notify parent
      lastNotifiedCoordsRef.current = initialCoordinates;
    }
  }, [initialCoordinates, mapLoaded, updateAddressFromCoordinates]);

  // Load Google Maps API
  useEffect(() => {
    if (!apiKey) {
      setMapError("No API key provided. Map will have limited functionality.");
      return;
    }

    if (window.google?.maps || window.mapApiLoaded) {
      setMapLoaded(true);
      return;
    }

    if (window.mapApiError) {
      setMapError("Failed to load Google Maps API. Please check your API key.");
      return;
    }

    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com"]'
    );
    if (existingScript) {
      const checkIfLoaded = setInterval(() => {
        if (window.google?.maps) {
          setMapLoaded(true);
          clearInterval(checkIfLoaded);
        }
      }, 100);

      return () => clearInterval(checkIfLoaded);
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      window.mapApiLoaded = true;
      setMapLoaded(true);
    };

    script.onerror = () => {
      window.mapApiError = true;
      setMapError("Failed to load Google Maps API. Please check your API key.");
    };

    document.head.appendChild(script);

    return () => {
      if (
        !window.google?.maps &&
        !window.mapApiLoaded &&
        document.head.contains(script)
      ) {
        document.head.removeChild(script);
      }
    };
  }, [apiKey]);

  // Initialize map when API is loaded
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !window.google?.maps) return;

    try {
      // Initialize map
      const map = new window.google.maps.Map(mapRef.current, {
        center: coordinates,
        zoom,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      mapInstanceRef.current = map;

      // Initialize marker
      const marker = new window.google.maps.Marker({
        position: coordinates,
        map,
        draggable: !disableDragging,
        animation: window.google.maps.Animation.DROP,
      });
      markerRef.current = marker;

      // Initialize geocoder
      geocoderRef.current = new window.google.maps.Geocoder();

      // Add event listeners
      if (!disableDragging) {
        marker.addListener("dragend", () => {
          const position = marker.getPosition();
          if (position) {
            const newCoords = {
              lat: position.lat(),
              lng: position.lng(),
            };

            // Update state without re-notifying parent yet
            setCoordinates(newCoords);

            // This will update the address and notify parent
            updateAddressFromCoordinates(newCoords);
          }
        });
      }

      map.addListener("click", (e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          marker.setPosition({ lat, lng });

          const newCoords = { lat, lng };

          // Update state without re-notifying parent yet
          setCoordinates(newCoords);

          // This will update the address and notify parent
          updateAddressFromCoordinates(newCoords);
        }
      });

      // Initialize search autocomplete
      if (withSearch && searchInputRef.current) {
        const autocomplete = new window.google.maps.places.Autocomplete(
          searchInputRef.current,
          { types: ["geocode", "establishment"] }
        );

        autocomplete.bindTo("bounds", map);
        autoCompleteRef.current = autocomplete;

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();

          if (place?.geometry?.location) {
            const newCoordinates = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };

            map.setCenter(newCoordinates);
            marker.setPosition(newCoordinates);

            // Update state
            setCoordinates(newCoordinates);

            const formattedAddress = place.formatted_address || "";
            setCurrentAddress(formattedAddress);
            setSearchValue("");

            // Store the last notified coordinates
            lastNotifiedCoordsRef.current = newCoordinates;

            // Notify parent directly in this case since we already have the address
            if (onLocationChange) {
              onLocationChange(newCoordinates, formattedAddress);
            }
          }
        });
      }

      if (!lastNotifiedCoordsRef.current) {
        updateAddressFromCoordinates(coordinates);
      }
    } catch (error) {
      toast.error("Failed to initialize map. Please try again later.");
    }

    // This effect should only run once when the map is loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!searchValue.trim() || !geocoderRef.current || !window.google?.maps)
      return;

    setIsLoading(true);

    geocoderRef.current.geocode(
      { address: searchValue },
      (
        results: google.maps.GeocoderResult[] | null,
        status: google.maps.GeocoderStatus
      ) => {
        setIsLoading(false);

        if (status === "OK" && results && results[0]?.geometry?.location) {
          const location = results[0].geometry.location;
          const newCoordinates = {
            lat: location.lat(),
            lng: location.lng(),
          };

          if (mapInstanceRef.current && markerRef.current) {
            mapInstanceRef.current.setCenter(newCoordinates);
            markerRef.current.setPosition(newCoordinates);
          }

          // Update state
          setCoordinates(newCoordinates);

          const formattedAddress = results[0].formatted_address || "";
          setCurrentAddress(formattedAddress);
          setSearchValue("");

          // Store the last notified coordinates
          lastNotifiedCoordsRef.current = newCoordinates;

          // Notify parent directly
          if (onLocationChange) {
            onLocationChange(newCoordinates, formattedAddress);
          }
        }
      }
    );
  };

  return (
    <div className={`flex flex-col gap-2 w-full ${className}`}>
      {withSearch && (
        <form onSubmit={handleSearchSubmit} className="relative">
          <input
            ref={searchInputRef}
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search for a location..."
            className="w-full p-2 pl-10 border border-gray-300 rounded focus:ring-2 focus:ring-blue-300 focus:border-blue-300 outline-none transition-all"
            disabled={!mapLoaded || !!mapError}
          />
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          {isLoading && (
            <div className="absolute right-3 top-2.5">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
            </div>
          )}
        </form>
      )}

      <div
        ref={mapRef}
        className={`relative ${height} w-full rounded-lg overflow-hidden bg-gray-100 border border-gray-200`}
      >
        {!mapLoaded && !mapError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="animate-spin h-8 w-8 border-3 border-blue-500 border-t-transparent rounded-full" />
          </div>
        )}

        {mapError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 p-4 text-center">
            <AlertCircle className="text-red-500 mb-2" size={24} />
            <p className="text-red-600 font-medium">{mapError}</p>
            <p className="text-gray-600 text-sm mt-1">
              Please check your API key or try again later.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapLocationWidget;
